import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouteHistoryListApi } from "../../../services/routeHistoryApis";

import PageHeader from "../../../components/common/PageHeader/PageHeader";
import SimpleSearchBox from "../../../components/common/Search/SimpleSearch";
import Sort from "../../../components/common/Sort/Sort";
import SelectedItemsCount from "../../../components/common/TotalCount/SelectedItemsCount";
import { useCheckboxClick } from "../../../checkboxclick";

import EmptyState from "../../../components/common/EmptyState/EmptyState";
import NoRoute from "../../../assets/empty-states/Route/NoRoute.svg";
import ExportLabel from "../../../components/common/ExportLabel/Export";
import { formatDate } from "../../../utils/dates";
import useFilterStore from "../../../store/useFilterStore";
import { useTranslation } from "react-i18next";

const RouteHistoryList = () => {
  const { t } = useTranslation();
  const [routeHistory, setRouteHistory] = useState([]);
  const navigate = useNavigate();
  const pageKey = "route_history_list";
  const [meta, setMeta] = useState({});
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);

  const [searchParams, setSearchParams] = useState(
    savedFilters?.searchParams || { term: "" },
  );

  const [selectedFilterOption, setSelectedFilterOption] = useState(
    savedFilters?.selectedFilterOption || "Default",
  );

  const [selectedSortOption, setSelectedSortOption] = useState(
    savedFilters?.selectedSortOption || "Newest",
  );

  const handleSearchSelect = (term) => {
    setSearchParams({ term });
  };

  const sortOptions = ["Newest", "Oldest"];
  const fetchRouteHistory = async () => {
    try {
      const params = {
        sort: selectedSortOption.toLowerCase(),
      };

      if (searchParams?.term) {
        params.search = searchParams.term;
      }

      if (selectedFilterOption !== "Default") {
        params.filter = selectedFilterOption.toLowerCase();
      }

      const response = await RouteHistoryListApi(params);

      setRouteHistory(response?.data?.data || []);
      setMeta(response?.data?.meta || {}); // ⭐ ADD THIS
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) navigate("/error");
      setRouteHistory([]);
      setMeta({ has_data: false });
    }
  };

  useEffect(() => {
    fetchRouteHistory();
  }, [searchParams, selectedSortOption, selectedFilterOption]);

  useEffect(() => {
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
  }, [searchParams, selectedSortOption, selectedFilterOption]);

  const {
    isParentSelected,
    selectedItems,
    handleParentCheckboxChange,
    handleChildCheckboxChange,
  } = useCheckboxClick(routeHistory);

  // const hasFilteredState =
  //   searchParams?.term ||
  //   selectedFilterOption !== "Default";
  // console.log("searchParams:", searchParams);
  return (
    <div className="whole-container">
      {/* HEADER */}
      <div className="path-header">
        <PageHeader
          breadcrumbs={[
            { label: "Route Management" },
            { label: "Route History" },
          ]}
        />
        <div className="path-header-buttons">
          <ExportLabel />
        </div>
      </div>

      {/* TOP BAR */}
      <div className="pagename-container">
        <div className="sub-page-left">
          <div className="page-name">{t("Route History")}</div>

          <SimpleSearchBox
            placeholder="Search"
            defaultValue={searchParams?.term}
            onSearchSelect={handleSearchSelect}
          />

          <SelectedItemsCount count={selectedItems.length} />
        </div>

        <div className="sub-head-r-side-icons">
          <Sort
            options={sortOptions}
            defaultValue={selectedSortOption}
            onOptionSelect={setSelectedSortOption}
          />
        </div>
      </div>

      {/* TABLE */}
      <div
        className={`table-container ${routeHistory.length === 0 ? "no-hover-table" : ""}`}
      >
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>
                <input
                  type="checkbox"
                  className="table-selectbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                {t("Route Name")}
              </th>
              <th>{t("Salesperson")}</th>
              <th>{t("Date")}</th>
              <th />
            </tr>
          </thead>

          <tbody className="common-table-tbody">
            {routeHistory.length ? (
              routeHistory.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/routes/history/details/${item.id}`)}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="table-selectbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleChildCheckboxChange(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {item.route_name}
                  </td>
                  <td>{item.sales_person_name}</td>
                  <td>{formatDate(item.date)}</td>
                  <td>
                    <div className="actions common-action"></div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ padding: 0, border: "none" }}>
                  <EmptyState
                    hasData={meta?.has_data}
                    image={NoRoute}
                    heading={t("No Route History Found")}
                    subText={t(
                      "Route history will appear here once routes are completed and tracked.",
                    )}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RouteHistoryList;
